import React, { useState, useEffect, useRef } from "react";
import { GoX, GoSearch, GoCalendar } from "react-icons/go";
import elasticsearchService from "../../services/elasticsearchService";

import {
  ModalOverlay,
  IndexDocsModalContainer,
} from "./styles";

/**
 * IndexDetailsModal — modal grande (1100px) dedicado à listagem de documentos
 * de um índice. Search com debounce + infinite scroll preservados do legacy.
 */
export default function IndexDetailsModal({ index, onClose }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [expandedDocs, setExpandedDocs] = useState(new Set());
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef(null);

  // Search debounce + reset
  useEffect(() => {
    const timer = setTimeout(() => {
      setDocuments([]);
      setHasMore(true);
      fetchDocuments(true);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          fetchDocuments(false);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) observer.observe(observerTarget.current);

    return () => {
      if (observerTarget.current) observer.unobserve(observerTarget.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore, documents.length]);

  const fetchDocuments = async (reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);

    try {
      const from = reset ? 0 : documents.length;
      const size = 20;
      const query = searchQuery.trim() || "*";

      const response = await elasticsearchService.searchDocuments(
        index.name,
        query,
        from,
        size
      );

      if (response.success) {
        const newDocs = response.data.documents || [];

        if (reset) setDocuments(newDocs);
        else setDocuments((prev) => [...prev, ...newDocs]);

        setTotal(response.data.total || 0);
        setHasMore(newDocs.length === size);
      }
    } catch (error) {
      console.error("Erro ao buscar documentos:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const toggleDocExpansion = (docId) => {
    setExpandedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const renderFieldValue = (value) => {
    if (value === null || value === undefined) return "—";
    if (typeof value === "object") return JSON.stringify(value);
    if (typeof value === "string" && value.length > 80)
      return value.substring(0, 80) + "...";
    return String(value);
  };

  const getDocumentDate = (doc) => {
    const source = doc._source || {};
    const dateFields = [
      "created_at",
      "createdAt",
      "timeStamp",
      "timestamp",
      "data",
      "date",
      "dhEmi",
      "dhRecbto",
    ];

    for (const field of dateFields) {
      if (source[field]) {
        try {
          const date = new Date(source[field]);
          if (!isNaN(date.getTime())) {
            return date.toLocaleString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
          }
        } catch {}
      }
    }

    if (source.data_info) {
      try {
        const date = new Date(parseInt(source.data_info));
        if (!isNaN(date.getTime())) {
          return date.toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      } catch {}
    }

    return null;
  };

  const healthClass = index.isArchived ? "archived" : index.health;

  return (
    <ModalOverlay onClick={onClose}>
      <IndexDocsModalContainer onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="docs-header">
          <div className="docs-title">
            <span className="eyebrow">DOCUMENTOS DO ÍNDICE</span>
            <h2 title={index.name}>{index.name}</h2>
            <div className="docs-meta">
              <span className={`docs-meta-item ${healthClass}`}>
                <span className="meta-dot" />
                {index.isArchived ? "arquivado" : index.health}
              </span>
              <span className="docs-meta-sep">·</span>
              <span>
                {total > 0
                  ? total.toLocaleString("pt-BR")
                  : (index.docsCount || 0).toLocaleString("pt-BR")}{" "}
                documentos
              </span>
              <span className="docs-meta-sep">·</span>
              <span>{index.storeSize || "—"}</span>
              {documents.length > 0 && (
                <>
                  <span className="docs-meta-sep">·</span>
                  <span>{documents.length} carregados</span>
                </>
              )}
            </div>
          </div>
          <button className="modal-close" onClick={onClose} title="Fechar">
            <GoX />
          </button>
        </div>

        {/* BODY */}
        <div className="docs-body">
          {/* Search */}
          <div className="docs-search">
            <GoSearch />
            <input
              type="text"
              placeholder="Buscar documentos... (busca ao digitar)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* List */}
          {loading && documents.length === 0 ? (
            <div className="docs-loading">Carregando documentos...</div>
          ) : (
            <>
              <div className="documents-list">
                {documents.map((doc) => {
                  const docId = doc._id;
                  const source = doc._source || {};
                  const isExpanded = expandedDocs.has(docId);
                  const docDate = getDocumentDate(doc);
                  const previewFields = Object.entries(source).slice(0, 4);

                  return (
                    <div
                      className="doc-card"
                      key={`${docId}-${doc._index}`}
                      onClick={() => toggleDocExpansion(docId)}
                    >
                      <div className="doc-header">
                        <div className="doc-id-group">
                          <span className="doc-id">ID: {docId}</span>
                          {docDate && (
                            <span className="doc-date">
                              <GoCalendar />
                              {docDate}
                            </span>
                          )}
                        </div>
                        {doc._score != null && (
                          <span className="doc-score">
                            Score {doc._score.toFixed(2)}
                          </span>
                        )}
                      </div>

                      <div className="doc-preview">
                        {previewFields.map(([key, value]) => (
                          <div key={key} className="field">
                            <span className="field-name">{key}</span>
                            <span className="field-value">
                              {renderFieldValue(value)}
                            </span>
                          </div>
                        ))}
                      </div>

                      {isExpanded && (
                        <div className="doc-full">
                          <pre>{JSON.stringify(source, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {documents.length === 0 && !loading && (
                <div className="docs-loading">
                  {searchQuery
                    ? "Nenhum documento encontrado para esta busca"
                    : "Nenhum documento encontrado"}
                </div>
              )}

              {hasMore && documents.length > 0 && (
                <div ref={observerTarget} style={{ padding: "12px 0" }}>
                  {loadingMore && (
                    <div className="docs-loading">Carregando mais...</div>
                  )}
                </div>
              )}

              {!hasMore && documents.length > 0 && (
                <div className="docs-footer-info">
                  Todos os documentos foram carregados ({documents.length} de{" "}
                  {total})
                </div>
              )}
            </>
          )}
        </div>
      </IndexDocsModalContainer>
    </ModalOverlay>
  );
}
